import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FiUsers, FiMap, FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { formatarData } from '../utils/dateUtils';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const assentosData = stats?.assentos ? [
    { name: 'Disponíveis', value: stats.assentos.disponiveis || 0 },
    { name: 'Reservados', value: stats.assentos.reservados || 0 },
    { name: 'Ocupados', value: stats.assentos.ocupados || 0 },
  ] : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-white mt-1">Visão geral do sistema</p>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-sky-50 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">Total de Passageiros</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalPassageiros || 0}</p>
            </div>
            <div className="bg-primary-100 p-3 rounded-full">
              <FiUsers className="text-primary-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-sky-50 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">Total de Viagens</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.totalViagens || 0}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FiMap className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-sky-50 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">Assentos Disponíveis</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.assentos?.disponiveis || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FiTrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-sky-50 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">Receita Total</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                R$ {stats?.receita?.receita_total?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <FiDollarSign className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-sky-50 rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Status dos Assentos</h2>
          {assentosData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={assentosData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={90} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {assentosData.map((entry, index) => {
                    const colors = ['#10b981', '#f59e0b', '#ef4444'];
                    return <Cell key={`cell-${index}`} fill={colors[index]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-600 text-center py-12">Nenhum dado disponível</p>
          )}
        </div>

        <div className="bg-sky-50 rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Receita</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <span className="text-gray-800">Receita Paga</span>
              <span className="text-2xl font-bold text-green-600">
                R$ {stats?.receita?.receita_paga?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
              <span className="text-gray-800">Receita Pendente</span>
              <span className="text-2xl font-bold text-yellow-600">
                R$ {stats?.receita?.receita_pendente?.toFixed(2) || '0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Viagens Recentes */}
      {stats?.viagensRecentes && stats.viagensRecentes.length > 0 && (
        <div className="bg-sky-50 rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Viagens Recentes</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sky-200">
              <thead className="bg-sky-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Título</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Destino</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data Ida</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Valor</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-sky-200">
                {stats.viagensRecentes.map((viagem) => (
                  <tr key={viagem.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{viagem.titulo}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{viagem.destino}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatarData(viagem.data_ida)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      R$ {parseFloat(viagem.valor).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
